import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Alert,
  Card,
  CardContent,
  Grid,
  Chip,
} from '@mui/material';
import {jwtDecode} from 'jwt-decode';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/constants';
import { usePermissions } from '../../context/PermissionContext';
import Loader from '../General/Loader'; // Adjust the import path as needed
import BackButton from '../../components/BackButton';

// Cashback reward is picked at random only from the first 5 slots; slots 6-11 are display only.
const CASHBACK_SLOT_COUNT = 5;

const SetRandom = () => {
  const [randomSequence, setRandomSequence] = useState([]);
  const [newRandomSequence, setNewRandomSequence] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState('');
  const [snackSeverity, setSnackSeverity] = useState('success');
  const { can } = usePermissions();
  const canEdit = can('master.cashbackSequence.edit');

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (token) {
      try {
        jwtDecode(token);
        fetchRandomSequence();
      } catch (error) {
        console.error('Error decoding token:', error);
        setSnackSeverity('error');
        setSnackMessage('Authentication error. Please log in again.');
        setSnackOpen(true);
        setLoading(false);
      }
    } else {
      setSnackSeverity('error');
      setSnackMessage('Authentication required. Please log in.');
      setSnackOpen(true);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRandomSequence = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const decodedToken = jwtDecode(token);
      const userId = decodedToken.userId;

      const response = await axios.get(`${API_BASE_URL}/divisions/random-sequence/${userId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      let sequence = (response.data.randomSequence || []).map(item => item.value);
      if (sequence.length > 11) {
        sequence = sequence.slice(0, 11);
      }
      while (sequence.length < 11) {
        sequence.push((sequence.length + 1) * 5);
      }
      setRandomSequence(sequence);
      setNewRandomSequence([...sequence]); // Create a copy for editing
    } catch (error) {
      console.error('Error fetching random sequence:', error);
      setSnackSeverity('error');
      setSnackMessage('Failed to fetch random sequence.');
      setSnackOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (index, value) => {
    const updatedSequence = [...newRandomSequence];
    updatedSequence[index] = value;
    setNewRandomSequence(updatedSequence);
  };

  const handleUpdateClick = () => {
    if (!canEdit) return;
    setDialogOpen(true);
  };

  const handleConfirmUpdate = async () => {
    if (!canEdit) return;
    setDialogOpen(false);
    setLoading(true);
    try {
      const token = sessionStorage.getItem('token');
      const decodedToken = jwtDecode(token);
      const userId = decodedToken.userId;

      // Prepare the updates array for 11 sequence values
      const updates = [];
      for (let i = 0; i < 11; i++) {
        if (newRandomSequence[i] !== randomSequence[i]) {
          const value = parseFloat(newRandomSequence[i]);
          if (isNaN(value) || value < 0 || value > 100) {
            throw new Error(`Value at position ${i + 1} must be a number between 0 and 100`);
          }
          updates.push({ index: i, value });
        }
      }

      if (updates.length === 0) {
        setSnackSeverity('info');
        setSnackMessage('No changes to update.');
        setSnackOpen(true);
        setLoading(false);
        return;
      }

      const response = await axios.put(
        `${API_BASE_URL}/divisions/random-sequence/${userId}`,
        { updates },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setSnackSeverity('success');
      setSnackMessage(response.data.message || 'Random sequence updated successfully.');
      setSnackOpen(true);
      fetchRandomSequence();
    } catch (error) {
      console.error('Error updating random sequence:', error);
      setSnackSeverity('error');
      setSnackMessage(error.response?.data?.message || error.message || 'Failed to update random sequence.');
      setSnackOpen(true);
      setLoading(false);
    }
  };

  const handleSnackClose = () => {
    setSnackOpen(false);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <Box sx={{ position: 'relative', padding: 2 }}>
    {/* Place BackButton outside the Grid */}
    <BackButton />
    <br/>  <br/>
    <Box sx={{ maxWidth: "90%", mx: 'auto', mt: 5 }}>
      <Card sx={{ p: 3, boxShadow: 3 }}>
        <CardContent>
          <Typography variant="h4" gutterBottom sx={{ fontFamily: 'Merriweather, serif' }}>
            Set Game Card Offer Sequence (11 Randoms + Bumper 100%)
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Configure the 11 percentage options for customer game cards. The 12th option is automatically set to <strong>100% Bumper Cashback</strong>.
          </Typography>
          <Alert severity="info" sx={{ mb: 3 }}>
            The cashback reward is chosen <strong>at random from the first {CASHBACK_SLOT_COUNT} values</strong> (Slots 1-{CASHBACK_SLOT_COUNT}).
            All 11 values still appear on the customer's game card; Slots {CASHBACK_SLOT_COUNT + 1}-11 are display only.
          </Alert>
          {newRandomSequence.length > 0 ? (
            <>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
                Random Percentages (11 Slots) + Bumper 100%:
              </Typography>
              <Grid container spacing={2}>
                {newRandomSequence.slice(0, 11).map((value, index) => {
                  const usedForCashback = index < CASHBACK_SLOT_COUNT;
                  return (
                    <Grid item xs={6} sm={4} md={2} key={index}>
                      <TextField
                        label={`Slot ${index + 1} (%)`}
                        variant="outlined"
                        value={value}
                        disabled={!canEdit}
                        style={{ width: '100%' }}
                        onChange={(e) => handleInputChange(index, e.target.value)}
                        InputProps={{ inputProps: { min: 0, max: 100 } }}
                        sx={usedForCashback ? {
                          '& .MuiOutlinedInput-root': { backgroundColor: '#f0fdf4' },
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#22c55e' },
                        } : {
                          '& .MuiOutlinedInput-root': { backgroundColor: '#f8fafc' },
                        }}
                      />
                      <Chip
                        size="small"
                        label={usedForCashback ? 'Used for cashback (random pick)' : 'Display only'}
                        color={usedForCashback ? 'success' : 'default'}
                        variant={usedForCashback ? 'filled' : 'outlined'}
                        sx={{ mt: 0.75, maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.25, fontSize: '0.68rem' } }}
                      />
                    </Grid>
                  );
                })}
                {/* 12th Bumper Slot (Read-Only 100%) */}
                <Grid item xs={6} sm={4} md={2}>
                  <TextField
                    label="Slot 12 (Bumper %)"
                    variant="outlined"
                    value="100%"
                    disabled
                    style={{ width: '100%' }}
                    sx={{
                      '& .MuiInputBase-input.Mui-disabled': {
                        color: '#d97706',
                        fontWeight: 'bold',
                        WebkitTextFillColor: '#d97706',
                      },
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#fef3c7',
                        borderColor: '#f59e0b',
                      }
                    }}
                  />
                </Grid>
              </Grid>
              {canEdit && (
                <>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleUpdateClick}
                    sx={{ mt: 3, fontWeight: 700, px: 3 }}
                  >
                    Update Random Sequence
                  </Button>
                </>
              )}
            </>
          ) : (
            <Typography variant="body1" color="textSecondary">
              No random sequence set.
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose}>
        <DialogTitle>Confirm Update</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to update the random sequence?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleConfirmUpdate} color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Notification */}
      <Snackbar
        open={snackOpen}
        autoHideDuration={6000}
        onClose={handleSnackClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackClose} severity={snackSeverity} sx={{ width: '100%' }}>
          {snackMessage}
        </Alert>
      </Snackbar>
    </Box>
    </Box>
  );
};

export default SetRandom;
